import { useMemo, useEffect } from "react";
import { useDashboardStatsStore } from "@store/dashboardStatsStore";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";

const STATUS_CONFIG: { key: string; label: string; color: string }[] = [
  { key: "a_preparer", label: "À préparer", color: "#6366F1" },
  { key: "commande", label: "Commande", color: "#8B5CF6" },
  { key: "pret", label: "Prêt", color: "#A78BFA" },
  { key: "reception", label: "Réception", color: "#0EA5E9" },
  { key: "en_cours", label: "En cours", color: "#06B6D4" },
  { key: "devis_initial", label: "Devis initial", color: "#F59E0B" },
  { key: "devis_supp", label: "Devis supp.", color: "#FBBF24" },
  { key: "accord", label: "Accord", color: "#10B981" },
  { key: "acompte", label: "Acompte", color: "#34D399" },
  { key: "fin_de_travaux", label: "Fin travaux", color: "#F97316" },
  { key: "facturation", label: "Facturation", color: "#EF4444" },
  { key: "solde", label: "Solde", color: "#F87171" },
  { key: "solde_paye", label: "Soldé payé", color: "#FB923C" },
  { key: "disponible", label: "Disponible", color: "#22C55E" },
  { key: "restitue", label: "Restitué", color: "#6B7280" },
];

export default function OrdresByStatusWidget() {
  const orgData = useDataStore((s) => s.orgData);
  const dashStats = useDashboardStatsStore((s) => s.stats);
  const fetchStats = useDashboardStatsStore((s) => s.fetchStats);

  useEffect(() => {
    if (orgData?.id) fetchStats(orgData.id);
  }, [orgData?.id]);

  const byStatus = dashStats?.by_status ?? {};

  const statusCounts = useMemo(() => {
    return STATUS_CONFIG.map((s) => ({ ...s, count: byStatus[s.key] || 0 }));
  }, [byStatus]);

  const total = dashStats?.total ?? 0;
  const active = useMemo(() => statusCounts.filter((s) => s.count > 0), [statusCounts]);

  // Donut chart params
  const RADIUS = 52;
  const STROKE = 12;
  const CENTER = 66;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const segments = useMemo(() => {
    if (total === 0) return [];
    let offset = 0;
    return active.map((s) => {
      const pct = s.count / total;
      const length = pct * CIRCUMFERENCE;
      const gap = active.length > 1 ? 3 : 0;
      const seg = { ...s, pct, dashOffset: offset, dashLength: Math.max(length - gap, 1) };
      offset += length;
      return seg;
    });
  }, [active, total]);

  return (
    <div className="flex flex-col bg-white border rounded-xl p-5 gap-4 shadow-lg">
      <div className="flex flex-row justify-between items-center">
        <div className="font-semibold text-black">Répartition par statut</div>
        <button
          onClick={() => navigateTo({ route: "/ordres" })}
          className="text-h3 text-neutral-400 hover:text-neutral-600"
        >
          Voir tout
        </button>
      </div>

      {total === 0 ? (
        <div className="text-h3 text-neutral-400 py-2">Aucun ordre</div>
      ) : (
        <div className="flex flex-row gap-5 items-start">
          {/* Donut */}
          <div className="flex-shrink-0 relative">
            <svg width={CENTER * 2} height={CENTER * 2} viewBox={`0 0 ${CENTER * 2} ${CENTER * 2}`}>
              {/* Background ring */}
              <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="#F3F4F6" strokeWidth={STROKE} />
              {/* Segments */}
              {segments.map((seg) => (
                <circle
                  key={seg.key}
                  cx={CENTER}
                  cy={CENTER}
                  r={RADIUS}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={STROKE}
                  strokeDasharray={`${seg.dashLength} ${CIRCUMFERENCE - seg.dashLength}`}
                  strokeDashoffset={-seg.dashOffset}
                  strokeLinecap="round"
                  className="transition-all duration-700"
                  style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
                />
              ))}
            </svg>
            {/* Center total */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-black">{total}</span>
              <span className="text-h4 text-neutral-400">ordres</span>
            </div>
          </div>

          {/* Legend list */}
          <div className="flex flex-col gap-1 flex-1 min-w-0 pt-1">
            {active.map((s) => (
              <div
                key={s.key}
                className="flex flex-row items-center gap-2.5 py-1.5 px-2 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => navigateTo({ route: `/ordres?status=${s.key}` })}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-h3 text-neutral-600 flex-1 truncate">{s.label}</span>
                <span className="text-h2 font-semibold text-black tabular-nums">{s.count}</span>
                <span className="text-h4 text-neutral-400 w-10 text-right tabular-nums">
                  {Math.round((s.count / total) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
